import type { CSSProperties, ForwardedRef, ReactElement } from 'react';
import React, { forwardRef } from 'react';
import type { InputProps, SelectProps } from '@mui/material';
import { TextField as MuiTextField } from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import type { OutlinedTextFieldProps } from '@mui/material/TextField';
import { isNil } from 'lodash';

import type { InputLabelProps } from '~/components/core/Atomic/InputLabel/InputLabel';
import SingleLineLabelWithTooltip, {
  SINGLE_LINE_LABEL_CLASS_NAME,
} from '~/components/core/Molecules/Fields/SingleLineLabelWithTooltip';
import { isLocalEnv, reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import { normalizeMultiSelectValue } from '../multiSelectValue';

// Mirrors MUI's own `isFilled` (non-null, non-empty-string, non-empty-array). We use it to decide
// when to pin the floating label shrunk — see the InputLabelProps merge below.
const isValueFilled = (value: unknown): boolean =>
  !isNil(value) && value !== '' && !(Array.isArray(value) && value.length === 0);

// MUI's Select swaps `target` for a plain `{ name, value }` object on its click path, so `value`
// here is whatever the Select holds (an array for `multiple`), not the DOM input's string.
type FieldChangeEvent = React.ChangeEvent & { target: { name?: string; value: unknown } };

export interface TextFieldProps<T> {
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  id?: string;
  InputLabelProps?: Partial<InputLabelProps>;
  multiline?: boolean;
  rows?: string | number;
  // Opt-in: keep a long floating label on one line (ellipsis) instead of letting it wrap through the
  // input underline and misalign the field, exposing the full text via the shared hover tooltip.
  // Covers every field routed through this component — plain inputs, selects, and the NumberFormat
  // number/monetary/year inputs; DatePickerField forwards the same flag for date/time.
  clampLabelToSingleLine?: boolean;
  value?: T;
  onChange?: (newValue: T, event: React.ChangeEvent) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  label?: string;
  className?: string;
  autoFocus?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  inputRef?: React.Ref<HTMLDivElement>;
  style?: CSSProperties;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  inputProps?: InputBaseComponentProps;
  name?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  // `rows` fixes the height; `minRows`/`maxRows` make it grow between bounds. MUI ignores the pair
  // when `rows` is also set, so a growing field must pass `minRows` and leave `rows` unset.
  minRows?: string | number;
  maxRows?: string | number;
  type?: 'number';
  preventOverrideOnChange?: boolean;
  inlineEditableField?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onMouseUp?: React.MouseEventHandler<HTMLDivElement>;
  showOnly?: boolean;
  size?: 'small' | 'medium';
  fullWidthField?: boolean;
  // Forwarded to MUI's Input as-is. Two sanctioned uses: the select components pass their own
  // adornments/classes through it, and a plain input passes `endAdornment` for an in-field control
  // (the editor's link dialog puts its token picker there). `inlineEditableField` sets its own
  // `InputProps` and, spreading later, wins over a caller's.
  InputProps?: Partial<InputProps>;

  // select only props
  select?: boolean; // should be used from the select component only
  SelectProps?: Partial<SelectProps>; // should be used from the select component only
  children?: React.ReactNode; // should be used from the select component only
  IconComponent?: React.ReactNode;
}

const TextFieldInner = <T,>(
  {
    value,
    showOnly,
    disabled,
    multiline,
    rows,
    fullWidth,
    helperText,
    onChange,
    onBlur,
    error,
    id,
    variant,
    InputLabelProps,
    label,
    className,
    autoFocus,
    inputRef,
    style,
    onFocus,
    placeholder,
    onClick,
    inputProps,
    name,
    minRows,
    maxRows,
    select,
    SelectProps,
    children,
    InputProps,
    type,
    inlineEditableField,
    size,
    preventOverrideOnChange,
    onKeyDown,
    onMouseUp,
    fullWidthField,
    clampLabelToSingleLine,
    ...rest
  }: TextFieldProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement => {
  if (Object.keys(rest).length > 0 && isLocalEnv()) {
    reportErrorInProductionOrThrow(
      `Additional unimplemented props was passed to TextField component (directly or from Multi/Single select fields), ${Object.keys(
        rest
      )}`,
      true
    );
  }

  // MUI ids a select's clickable element `mui-component-select-<name>`, but only when nothing sets
  // `SelectDisplayProps.id` — and MUI itself now always sets it, to a mount-order-dependent `mui-N`
  // that nothing can reliably select on. Set it here so the id stays the readable, name-derived one
  // that e2e's `openSelectMenu` helper and the select-opening unit tests locate by. A call site's own
  // `SelectDisplayProps` still wins, since it spreads last.
  const selectDisplayId = name || id;
  const selectPropsWithStableId =
    select && selectDisplayId
      ? {
          ...SelectProps,
          SelectDisplayProps: {
            id: `mui-component-select-${selectDisplayId}`,
            ...SelectProps?.SelectDisplayProps,
          },
        }
      : SelectProps;

  const selectProps = {
    select,
    SelectProps: selectPropsWithStableId,
    children,
  };
  const isMultipleSelect = !!(select && SelectProps?.multiple);
  const resolvedValue = isMultipleSelect ? (normalizeMultiSelectValue(value) as unknown as T) : value;
  const calcId = select && !SelectProps?.multiple ? undefined : id;
  const fieldName = name || id;

  const handleFieldChange = (event: FieldChangeEvent) => {
    if (!onChange) {
      return null;
    }
    if (!isMultipleSelect) {
      return onChange(event.target.value as T, event);
    }
    const newValue = normalizeMultiSelectValue(event.target.value);
    if (newValue === event.target.value) {
      return onChange(newValue as T, event);
    }
    // The autofill `handleChange` hands the hidden input's raw string through untouched, so repair
    // `target` here — before any consumer reads it — into the same `{ name, value }` shape MUI's own
    // click path builds. Clone through the prototype rather than mutating (v5 clones for the same
    // reason): the original event is left intact and React's synthetic API — `preventDefault`,
    // `stopPropagation`, `nativeEvent` — stays reachable for consumers that need it.
    const repairedEvent: FieldChangeEvent = Object.create(event, {
      target: {
        value: { name: event.target.name || fieldName, value: newValue },
        writable: true,
        enumerable: true,
        configurable: true,
      },
    });
    return onChange(newValue as T, repairedEvent);
  };

  const muiVariant = inlineEditableField ? 'outlined' : variant || 'standard';

  const editableDisplayProps: Partial<OutlinedTextFieldProps> = inlineEditableField
    ? select
      ? selectEditableFieldProps
      : editableFieldProps
    : {};
  // Lift any InputLabelProps out of editableDisplayProps so the label merge below is authoritative;
  // otherwise the later `{...editableDisplayProps}` spread would overwrite our merged label props.
  const { InputLabelProps: editableInputLabelProps, ...editableDisplayPropsRest } = editableDisplayProps;

  const fullWidthDisplayProps = fullWidthField ? fullWidthProps : {};

  const renderedLabel = clampLabelToSingleLine && label ? <SingleLineLabelWithTooltip label={label} /> : label;

  return (
    <MuiTextField
      disabled={disabled || showOnly}
      value={resolvedValue}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={preventOverrideOnChange ? onChange : handleFieldChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      id={calcId}
      name={fieldName}
      multiline={multiline}
      rows={rows}
      fullWidth={fullWidth}
      size={size || 'small'}
      // MUI shrinks the floating label only while it believes the input is "filled", and that belief
      // is frozen at mount (InputBase captures `isControlled = value != null` once). A Select that
      // mounts null/undefined and gets its value later — e.g. loaded async through Formik — never has
      // its filled-check re-run, so the label drops back over the value on blur. (MUI's docs flag the
      // same manual-shrink need for numeric/date inputs.) Pin shrink whenever the value is actually
      // filled (mirroring MUI's own `isFilled`); callers can still override via InputLabelProps.
      InputLabelProps={{
        htmlFor: calcId,
        ...(isValueFilled(resolvedValue) ? { shrink: true } : {}),
        ...editableInputLabelProps,
        ...InputLabelProps,
        // `pe-20` on every select clears the dropdown/clear adornment so the label never overlaps it
        className: cn(
          { 'pe-20': select, [SINGLE_LINE_LABEL_CLASS_NAME]: clampLabelToSingleLine },
          editableInputLabelProps?.className,
          InputLabelProps?.className
        ),
      }}
      variant={muiVariant}
      label={renderedLabel}
      className={className}
      autoFocus={autoFocus}
      ref={ref}
      inputRef={inputRef}
      style={style}
      onFocus={onFocus}
      placeholder={placeholder}
      onClick={onClick}
      inputProps={inputProps}
      minRows={minRows}
      maxRows={maxRows}
      type={type}
      onKeyDown={onKeyDown}
      onMouseUp={onMouseUp}
      InputProps={InputProps}
      {...selectProps}
      {...editableDisplayPropsRest}
      {...fullWidthDisplayProps}
      {...rest}
    />
  );
};

const TextField = forwardRef(TextFieldInner) as <T>(
  props: TextFieldProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReactElement;

const editableFieldProps: Partial<OutlinedTextFieldProps> = {
  InputProps: {
    classes: {
      notchedOutline: 'border-0 focus:border-1 rounded-3xl',
    },
  },
};

const selectEditableFieldProps: Partial<OutlinedTextFieldProps> = {
  InputProps: {
    classes: {
      notchedOutline: 'border-0 focus:border-1 rounded-3xl',
    },
  },
  InputLabelProps: {
    className: 'text-slate-800',
  },
  SelectProps: {
    classes: {
      outlined: 'focus:bg-transparent',
      iconOutlined: 'hidden',
    },
  },
};

const fullWidthProps = {
  classes: {
    root: 'w-full',
  },
};

export default TextField;
